
import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { TeacherFormData } from '@/types/teacher.types';
import { isAlphabetsOnly, isNumbersOnly, isValidPhone } from '@/utils/validation';

export const useTeacherFormState = () => {
  const [formData, setFormData] = useState<TeacherFormData>({
    id: uuidv4(),
    firstName: '',
    lastName: '',
    fatherName: '',
    gender: '',
    position: '',
    idNumber: '',
    email: '',
    phone: '',
    country: 'Afghanistan',
    city: '',
    address: '',
    joinDate: new Date().toISOString(),
    contractEndDate: '',
    salary: '',
    paymentStatus: '',
    departments: [],
    qualifications: [],
    documents: [],
    profileImage: '',
    history: [],
    age: '',
    dateOfBirth: '',
    maritalStatus: '',
    employmentType: '',
    status: 'Active',
    subjects: [],
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Validate text fields to only accept alphabets for specific fields
    if (['firstName', 'lastName', 'fatherName', 'city'].includes(name)) {
      // Only update if value is empty or contains only alphabets and spaces
      if (value === '' || isAlphabetsOnly(value)) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
      return;
    }
    
    // Validate numeric fields to only accept numbers
    if (['age', 'salary', 'idNumber'].includes(name)) {
      // Only update if value is empty or contains only numbers
      if (value === '' || isNumbersOnly(value)) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
      return;
    }

    // Special validation for phone numbers
    if (name === 'phone') {
      if (value === '' || isValidPhone(value)) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
      return;
    }
    
    // Email validation is handled by HTML5 input type, we just need to ensure no spaces
    if (name === 'email') {
      const noSpacesValue = value.replace(/\s/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: noSpacesValue
      }));
      return;
    }
    
    // Special case for dateOfBirth to automatically calculate age
    if (name === 'dateOfBirth' && value) {
      const birthDate = new Date(value);
      const today = new Date();
      let calculatedAge = today.getFullYear() - birthDate.getFullYear();
      
      // Adjust age if birthday hasn't occurred yet this year
      const hasBirthdayOccurredThisYear = 
        today.getMonth() > birthDate.getMonth() || 
        (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
      
      if (!hasBirthdayOccurredThisYear) {
        calculatedAge--;
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        age: calculatedAge.toString()
      }));
    } else {
      // For all other fields, update without validation
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  return { formData, setFormData, handleChange };
};
